import { Field } from 'formik'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { CitySearchLegacy } from 'components/CitySearchLegacy'
import { CountrySearchLegacy } from 'components/CountrySearchLegacy'
import { Flex, Grid } from 'components/Layout'
import { StateSearchLegacy } from 'components/StateSearchLegacy'

export const EMPTY_OPTIONS = {
  COUNTRY_SELECT: {
    title: '',
    isoCode: '',
  },
  CITY_SELECT: {
    title: '',
    value: '',
  },
  STATE_SELECT: {
    title: '',
    value: '',
  },
}

export const LocationField = ({
  isDisabled,
  field: {
    name: fieldName,
    value: { country, state, city = {} },
  },
  meta: { error },
  form: { setFieldValue, touched: formTouched, isValidating },
  isPortal,
  isWithCity,
  disabledFields,
  areRequiredFields = false,
  ...props
}) => {
  const touched = get(formTouched, fieldName, {})
  return (
    <Grid {...props}>
      <Flex>
        <Field name="country">
          {({ field: { name } }) => (
            <CountrySearchLegacy
              isDisabled={isDisabled || disabledFields?.country}
              isError={
                !isValidating &&
                touched?.country &&
                error?.country?.isoCode !== undefined
              }
              hint={
                !isValidating && touched?.country && error?.country?.isoCode
                  ? error?.country?.isoCode
                  : null
              }
              isRequiredField={areRequiredFields}
              onChange={(value) => {
                setFieldValue(`${fieldName}.country`, value)
                setFieldValue(`${fieldName}.state`, EMPTY_OPTIONS.STATE_SELECT)
                if (isWithCity) {
                  setFieldValue(`${fieldName}.city`, EMPTY_OPTIONS.CITY_SELECT)
                }
              }}
              value={country.isoCode ? country : null}
              menuPortalTarget={isPortal && document.body}
              name={`${fieldName}.${name}.isoCode`}
              hasHint
            />
          )}
        </Field>
      </Flex>
      {country.isoCode === 'us' && (
        <Flex>
          <Field name="state">
            {({ field: { name } }) => (
              <StateSearchLegacy
                isDisabled={isDisabled || disabledFields?.state}
                isError={
                  !isValidating &&
                  touched?.state &&
                  error?.state?.value !== undefined
                }
                hint={
                  !isValidating && touched?.state && error?.state?.value
                    ? error?.state?.value
                    : null
                }
                countryIsoCode={country.isoCode}
                onChange={(value) => {
                  setFieldValue(`${fieldName}.state`, value)
                  if (isWithCity) {
                    setFieldValue(
                      `${fieldName}.city`,
                      EMPTY_OPTIONS.CITY_SELECT
                    )
                  }
                }}
                value={state}
                name={`${fieldName}.${name}.value`}
                hasHint
                menuPortalTarget={isPortal && document.body}
                isRequiredField={areRequiredFields}
              />
            )}
          </Field>
        </Flex>
      )}
      {isWithCity && (
        <Flex>
          <Field name="city">
            {({ field: { name } }) => (
              <CitySearchLegacy
                isDisabled={isDisabled || disabledFields?.city}
                isError={
                  !isValidating &&
                  touched?.city &&
                  error?.city?.value !== undefined
                }
                hint={
                  !isValidating && touched?.city && error?.city
                    ? error?.city?.value
                    : null
                }
                countryIsoCode={country.isoCode}
                onChange={(value) => {
                  setFieldValue(`${fieldName}.city`, value)
                }}
                value={city}
                name={`${fieldName}.${name}.value`}
                menuPortalTarget={isPortal && document.body}
                isRequiredField={areRequiredFields}
              />
            )}
          </Field>
        </Flex>
      )}
    </Grid>
  )
}

LocationField.defaultProps = {
  isWithCity: true,
}

LocationField.propTypes = {
  isWithCity: PropTypes.bool,
  isDisabled: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.shape({
      country: PropTypes.shape({
        title: PropTypes.string.isRequired,
        isoCode: PropTypes.string.isRequired,
      }),
      state: PropTypes.shape({
        title: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
      city: PropTypes.shape({
        title: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    }).isRequired,
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    touched: PropTypes.shape({
      country: PropTypes.bool,
      state: PropTypes.bool,
      city: PropTypes.bool,
    }),
    isValidating: PropTypes.bool,
  }),
  meta: PropTypes.shape({
    error: PropTypes.shape({
      country: PropTypes.shape({
        isoCode: PropTypes.string,
      }),
      state: PropTypes.shape({
        value: PropTypes.string,
      }),
      city: PropTypes.shape({
        value: PropTypes.string,
      }),
    }),
  }),
  isPortal: PropTypes.bool,
  disabledFields: PropTypes.shape({
    country: PropTypes.bool,
    state: PropTypes.bool,
    city: PropTypes.bool,
  }),
  areRequiredFields: PropTypes.bool,
}

export default LocationField
